import type { User, UserIdentity } from "@supabase/supabase-js";

const STORAGE_KEY = "heybodhi.oauthLinkConfirmed";

/**
 * GoTrue stamps an identity's `created_at` and `last_sign_in_at` together when
 * the identity is created, so "created during this very sign-in" means the two
 * are within a moment of each other. The window absorbs clock and
 * serialisation skew.
 */
const FRESH_IDENTITY_TOLERANCE_MS = 5_000;

/**
 * The freshness check above only proves an identity's own two timestamps are
 * close together — it says nothing about whether that pair was stamped just
 * now or months ago. A months-old link must never be able to look fresh
 * again, so the sign-in itself must also have happened recently, regardless
 * of whether GoTrue ever advances `last_sign_in_at` on later sign-ins.
 */
const RECENT_SIGN_IN_WINDOW_MS = 10 * 60 * 1000;

function toMillis(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** The identity that signed in most recently, or null when it cannot be determined. */
function latestIdentity(user: User | null | undefined): UserIdentity | null {
  const identities = user?.identities;
  if (!Array.isArray(identities) || identities.length === 0) return null;

  let best: UserIdentity | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const candidate of identities) {
    if (typeof candidate !== "object" || candidate === null) continue;
    const ms = toMillis(candidate.last_sign_in_at);
    if (ms === null || ms <= bestMs) continue;
    bestMs = ms;
    best = candidate;
  }
  return best;
}

/** Provider behind the current session — `email`, `google`, `azure` — or null if unknown. */
export function getCurrentSignInProvider(user: User | null | undefined): string | null {
  return latestIdentity(user)?.provider ?? null;
}

export interface ProviderLinkConfirmation {
  provider: string;
  email: string;
}

/**
 * Detects the case AE-206 must confirm with a one-time code: an OAuth identity
 * that was just linked onto an account which already existed under a different
 * identity.
 *
 * This is a user-facing confirmation, not a security boundary: Supabase links
 * the identity and issues the session server-side before any of this code
 * runs, so nothing here can prevent the link — it can only ask the user to
 * confirm it after the fact. Every uncertain input therefore returns null:
 * skipping an advisory confirmation is a small cost, while a false positive
 * would lock every OAuth user out of the product.
 */
export function requiresProviderLinkConfirmation(
  user: User | null | undefined
): ProviderLinkConfirmation | null {
  const current = latestIdentity(user);
  if (!current || current.provider === "email") return null;

  const email = (user?.email ?? "").trim().toLowerCase();
  if (!email) return null;

  const createdMs = toMillis(current.created_at);
  const signedInMs = toMillis(current.last_sign_in_at);
  if (createdMs === null || signedInMs === null) return null;
  if (Math.abs(signedInMs - createdMs) > FRESH_IDENTITY_TOLERANCE_MS) return null;
  if (Math.abs(Date.now() - signedInMs) > RECENT_SIGN_IN_WINDOW_MS) return null;

  // An account that pre-existed this link has at least one clearly older identity.
  const accountPreExisted = (user?.identities ?? []).some((candidate) => {
    if (typeof candidate !== "object" || candidate === null) return false;
    if (candidate === current) return false;
    const otherCreatedMs = toMillis(candidate.created_at);
    return otherCreatedMs !== null && otherCreatedMs < createdMs - FRESH_IDENTITY_TOLERANCE_MS;
  });
  if (!accountPreExisted) return null;

  if (isProviderLinkConfirmed(email, current.provider)) return null;
  return { provider: current.provider, email };
}

function keyFor(email: string, provider: string): string {
  return `${email.trim().toLowerCase()}::${provider}`;
}

function readConfirmed(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    // Unreadable or corrupted storage means "nothing confirmed yet".
    return [];
  }
}

export function isProviderLinkConfirmed(email: string, provider: string): boolean {
  return readConfirmed().includes(keyFor(email, provider));
}

/**
 * Remembers the confirmation for this device. Per-device is deliberate: it also
 * protects against the inverse failure — if `last_sign_in_at` never advanced
 * past `created_at`, the freshness check would hold on every sign-in and this
 * memo is what keeps the user from confirming on every single login.
 */
export function markProviderLinkConfirmed(email: string, provider: string): void {
  try {
    const next = new Set(readConfirmed());
    next.add(keyFor(email, provider));
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // Device storage unavailable — the user will simply be asked again next time.
  }
}
